/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useRef } from 'react'
import { ChatType, UserChat } from 'interfaces/UserChat'
import ProfilePicture, { ProfilePictureRaw } from 'components/elements/ProfilePicture'
import styled from 'styled-components'
import AddCircleIcon from 'assets/icons/webapp_add.svg'
import RefetchIcon from 'assets/icons/webapp_renew.svg'
import FilterListIcon from 'assets/icons/webapp_filter.svg'
import GroupMembersIcon from 'assets/icons/group-members.svg'
import LoadingArea from 'components/elements/LoadingArea'
import { useToggleState } from 'lib/hooks/useToggleState'
import CreateNewChatDialog from 'components/feature/Chat/UserChat/CreateNewChatDialog'
import isValidHttpUrl, { doesLinkIncludeAttachment } from 'lib/formatting/url'
import { formatDate, formatHourMinuteString, isSameDay } from 'lib/formatting/datetime'
import theme from 'stories/utils/theme'
import { Body1, Body2, StandardHeadline, Subtitle1, Subtitle2 } from 'stories/elements/Typography/Text'
import Spinner from 'components/elements/Spinner'
import InfiniteScroll from 'react-infinite-scroller'
import { Pagination } from 'interfaces/Pagination'
import DeleteIcon from 'assets/icons/webapp_delete.svg'
import UserChatService from 'services/UserChatService'
import { ApiError } from 'services/ApiService'
import _useTranslation from 'lib/hooks/_useTranslation'
import Stack from 'components/elements/Stack'
import { Form } from '@unform/web'
import { FormHandles } from '@unform/core'
import Checkbox from 'components/elements/Inputs/Checkbox'

const ChatListWrapper = styled.div`
    background-color: var(--gray-background-color);
    width: 100%;
    display: grid;
    grid-template-rows: auto 1fr auto;
    grid-gap: 0;
    height: 825px;

    box-shadow: 0px 0px 21px rgba(61, 62, 64, 0.08);

    text-align: center;
    border-radius: ${theme.borderRadius.normal};
    background-color: ${theme.colors.white};
    h3 {
        cursor: pointer;
        padding-top: 28px;
        border-top: 1px solid #ccc;
        &:hover {
            color: var(--secondary-color);
        }
    }
    li {
        margin: 0;
    }
    @media (max-width: 750px) {
        border-radius: 0;
    }
`

const ChatItem = styled.div<{ selected?: boolean }>`
    width: 100%;
    margin: 0 !important;
    position: relative;
    padding: 15px 10px;
    background-color: ${({ selected }) => (selected ? '#ddd' : '#fff')};
    cursor: pointer;
    border-bottom: 1px solid #ccc;
    display: grid;
    grid-template-columns: auto 1fr;
    &:last-child {
        border: none;
    }
    &:hover {
        background-color: #eee;
    }
`

const ChatListText = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;

    padding: 0;
    margin-left: 10px;
    span {
        font-size: 18px;
        font-weight: 600 !important;
    }
    p {
        max-width: 250px;
        white-space: nowrap;
        overflow: hidden;
        color: ${theme.colors.body1Black};
        text-overflow: ellipsis;
    }
`

const ChatDateWrapper = styled.div`
    && {
        margin: 0;
        position: absolute;
        right: 15px;
        top: 0px;
        color: var(--gray);
        font-size: 16px;
    }
`

const GroupIconWrapper = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: grid;
    place-content: center;
    background-color: ${theme.colors.white};
    border: 2px solid ${theme.colors.violetDarkerBackground};
`

const DeleteWrapper = styled.div`
    position: absolute;
    background-color: ${theme.colors.white};
    display: grid;
    place-content: center;
    height: 27.5px;
    width: 27.5px;
    border-radius: 50%;
    bottom: 2.5px;
    right: 3.5px;
    z-index: 3;

    @media (max-width: 1050px) {
        height: 30px;
        width: 30px;
        right: 10px;
    }
`

const ChatsWrapper = styled.div`
    margin-bottom: -20px;
    overflow-y: auto;
    .empty-chats {
        padding: 20px;
    }
`

const UnreadChatCountBubble = styled.div`
    position: absolute;
    right: 20px;
    top: 22.5px;
    width: 25px;
    height: 25px;
    background: ${theme.colors.deepSeaBlue};
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
`

const HeaderWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 0px;
    padding: 8px 15px;
    border-bottom: 1px solid #ccc;
    .actions {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 12.5px;
    }
    h1 {
        font-size: 18px;
        line-height: 24px;
        margin: 0px;
        font-weight: 600;
        color: ${theme.colors.subtitleBlack};
    }
    button {
        padding: 2px 5px;
    }
    svg {
        cursor: pointer;
        margin-top: 0px;
        font-size: 36px;
        color: var(--secondary-color);
    }
`

const FilterWrapper = styled.div`
    display: grid;
    padding: 5px 10px 0px 10px;
    height: 130px;
    border-bottom: 1px solid #ccc;
    animation: showFilter 0.5s 1 ease-in-out forwards;
    @keyframes showFilter {
        0% {
            opacity: 0;
            transform: translateY(-10px);
        }

        100% {
            opacity: 1;
            transform: translateY(0px);
        }
    }
`

interface Props {
    userChats: UserChat[] | undefined
    selectChat: (chat: UserChat | undefined) => void
    selectedChatUuid: string | undefined
    area: string
    fetchData: (pageNumber: number, showLoading?: boolean) => void
    pagination: Pagination
    refetchChatList: () => void
    handleCheckboxClicked: (checked: boolean, type: ChatType) => void
    listUnreadChats: boolean
    selectedChatType?: ChatType
}

function ChatList({
    userChats,
    selectChat,
    area,
    fetchData,
    selectedChatUuid,
    pagination,
    refetchChatList,
    handleCheckboxClicked,
    listUnreadChats,
    selectedChatType,
}: Props) {
    const [openNewChatDialog, toggleNewChatDialog] = useToggleState(false)
    const [showFilters, toggleFilters] = useToggleState(false)

    const formRef = useRef<FormHandles>(null)
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('chat')

    const previewMessage = (message: string): string => {
        if (isValidHttpUrl(message)) {
            if (doesLinkIncludeAttachment(message)) {
                return t('chat/attachmentPreviewMessage', {
                    message:
                        message.split('.').pop() === 'jpg' || message.split('.').pop() === 'png'
                            ? t('chat/attachementImage')
                            : t('chat/attachementFile'),
                })
            } else if (message.split('/')[3] === 'knows') {
                return t('chat/knowPreviewMessage', { message: message.split('/').pop() })
            } else {
                return t('chat/linkPreviewMessage', { message })
            }
        } else {
            return message
        }
    }

    const handleClose = () => {
        toggleNewChatDialog()
        fetchData(0, false)
    }

    const handleChatClicked = (userChat: UserChat | undefined) => userChat && selectChat(userChat)

    const isUnread = (unreadCount: number) => unreadCount > 0

    const returnMessageDate = (createDate: string) =>
        isSameDay(createDate, new Date()) ? formatHourMinuteString(createDate) : formatDate(createDate)

    const deleteChatItem = (chatUuid: string) => {
        UserChatService.deleteChat(chatUuid)
            .then(() => {
                fetchData(0, true)
                userChats?.length && selectChat(userChats[0])
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'know.delete')
                } else {
                    throw error
                }
            })
    }

    const returnChatListIcon = (chat: UserChat) => {
        if (chat.chatType === 'GROUP')
            return (
                <GroupIconWrapper>
                    <GroupMembersIcon />
                </GroupIconWrapper>
            )

        return chat?.chatPartner ? (
            <ProfilePicture user={chat.chatPartner} size={50} />
        ) : (
            <ProfilePictureRaw name="" profilePicture={chat.chatPictureUrl} size={50} />
        )
    }

    const handleUnreadMessage = (checked: boolean, type: ChatType) => {
        formRef.current?.setFieldValue(type === ChatType.Group ? 'listUnread1on1' : 'listUnreadGroupchats', false)
        handleCheckboxClicked(checked, type)
    }

    return (
        <ChatListWrapper>
            <Stack gutter={0}>
                <HeaderWrapper>
                    <StandardHeadline fontFamily="var(--font-inter)">Chats</StandardHeadline>
                    <div className="actions">
                        <FilterListIcon onClick={toggleFilters} />
                        <RefetchIcon onClick={refetchChatList} />
                        <AddCircleIcon onClick={toggleNewChatDialog} />
                    </div>
                </HeaderWrapper>
                {showFilters ? (
                    <FilterWrapper>
                        <Form
                            ref={formRef}
                            onSubmit={() => {}}
                            initialData={{
                                listUnreadGroupchats: listUnreadChats && selectedChatType === ChatType.Group,
                                listUnread1on1: listUnreadChats && selectedChatType === ChatType.OneOnOne,
                            }}
                        >
                            <Checkbox
                                label={
                                    <Body2 fontFamily="var(--font-inter)" fontWeight={400}>
                                        {t('chat/unreadGroupChatFilterLabel')}
                                    </Body2>
                                }
                                name="listUnreadGroupchats"
                                placeholder=""
                                noMargin
                                onUpdate={(checked) => {
                                    handleUnreadMessage(checked, ChatType.Group)
                                }}
                            />
                            <Checkbox
                                label={
                                    <Body2 fontFamily="var(--font-inter)" fontWeight={400}>
                                        {t('chat/unread1on1ChatFilterLabel')}
                                    </Body2>
                                }
                                name="listUnread1on1"
                                placeholder=""
                                noMargin
                                onUpdate={(checked) => {
                                    handleUnreadMessage(checked, ChatType.OneOnOne)
                                }}
                            />
                        </Form>
                    </FilterWrapper>
                ) : null}
            </Stack>

            <LoadingArea area={area}>
                <ChatsWrapper>
                    <InfiniteScroll
                        pageStart={0}
                        loadMore={fetchData}
                        hasMore={pagination.hasMorePages}
                        loader={<Spinner forceShow key={pagination.page} />}
                        useWindow={false}
                        threshold={50}
                    >
                        {userChats?.length ? (
                            userChats.map((chat) => (
                                <ChatItem
                                    key={chat.uuid}
                                    onClick={() => handleChatClicked(chat)}
                                    selected={selectedChatUuid === chat.uuid}
                                >
                                    <div>{returnChatListIcon(chat)}</div>
                                    <ChatListText>
                                        <Subtitle1
                                            fontFamily="var(--font-inter)"
                                            fontWeight={600}
                                            color={theme.colors.black}
                                        >
                                            {chat.chatPartner?.name ?? chat.chatPartner?.username ?? chat.name}
                                        </Subtitle1>
                                        <Body1
                                            color={
                                                isUnread(chat.unreadCount)
                                                    ? theme.colors.deepSeaBlue
                                                    : theme.colors.body1Black
                                            }
                                            fontFamily="var(--font-inter)"
                                            fontWeight={400}
                                        >
                                            {chat.lastMessage ? previewMessage(chat.lastMessage.message).slice(0, 35) : ''}
                                        </Body1>
                                    </ChatListText>

                                    <ChatDateWrapper>
                                        <Body2
                                            color={
                                                isUnread(chat.unreadCount)
                                                    ? theme.colors.deepSeaBlue
                                                    : theme.colors.body1Black
                                            }
                                            fontFamily="var(--font-inter)"
                                            fontWeight={400}
                                        >
                                            {chat.lastMessage ? returnMessageDate(chat.lastMessage.createdOn) : null}
                                        </Body2>
                                    </ChatDateWrapper>
                                    <DeleteWrapper onClick={() => deleteChatItem(chat.uuid)}>
                                        <DeleteIcon />
                                    </DeleteWrapper>
                                    {isUnread(chat.unreadCount) ? (
                                        <UnreadChatCountBubble>
                                            <Subtitle2 color={theme.colors.white} textAlign="center" fontWeight={600}>
                                                {chat.unreadCount}
                                            </Subtitle2>
                                        </UnreadChatCountBubble>
                                    ) : null}
                                </ChatItem>
                            ))
                        ) : (
                            <Body1 textAlign="center" className="empty-chats">
                                {t('chat/noChatsFound')}
                            </Body1>
                        )}
                    </InfiniteScroll>
                </ChatsWrapper>
            </LoadingArea>
            <CreateNewChatDialog open={openNewChatDialog} handleClose={handleClose} selectChat={selectChat} />
        </ChatListWrapper>
    )
}

export default ChatList
